import { memo, useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import ReactTooltip from 'react-tooltip';
import { NavLink, Link } from 'react-router-dom';
import {
  checkFeatures,
  checkFeatureToggles,
  checkPerms,
  sideBarShow,
  getCurrectWeekRange,
  isLimitedInspector,
  existsAnyNotExternalRole,
  notOutUsers,
  notOutUsersWithLimitedInspector,
  checkSingleRole
} from '../helpers/utils';
import useSidebarContext from '../hooks/useSidebarContext';
import { ReactComponent as RightIcon } from '../assets/images/arrow-right.svg';
import { ReactComponent as LeftIcon } from '../assets/images/arrow-left.svg';

import { ReactComponent as DashboardIcon } from '../assets/images/sidebar/ic_dashboard.svg';
import { ReactComponent as WorkordersIcon } from '../assets/images/sidebar/ic_work_order.svg';
import { ReactComponent as EcabinetIcon } from '../assets/images/sidebar/ic_ecabinet.svg';
import { ReactComponent as ScoreCardIcon } from '../assets/images/sidebar/scorecard.svg';
import { ReactComponent as AnalyticsIcon } from '../assets/images/sidebar/ic_analytics.svg';
import { ReactComponent as AnalyticsReportsIcon } from '../assets/images/sidebar/ic_analytics_report.svg';
import { ReactComponent as InspectionReportsIcon } from '../assets/images/sidebar/ic_inspection_report.svg';
import { ReactComponent as ATGIcon } from '../assets/images/sidebar/atg.svg';
import { ReactComponent as TestingIcon } from '../assets/images/sidebar/ic_testing.svg';
import { ReactComponent as CTIMSIcon } from '../assets/images/sidebar/ctims.svg';
import { ReactComponent as LMSIcon } from '../assets/images/sidebar/lms.svg';
import { ReactComponent as Inventory } from '../assets/images/sidebar/ic_inventory.svg';
import { ReactComponent as CalendarIcon } from '../assets/images/calendar-icon.svg';
import { ReactComponent as UnlockedIcon } from '../assets/images/sidebar/sidebar_unlock.svg';
import { ReactComponent as LockedIcon } from '../assets/images/sidebar/sidebar_lock.svg';
import { ReactComponent as IVIcon } from '../assets/images/sidebar/inventory_variance.svg';
import { ReactComponent as RDIcon } from '../assets/images/sidebar/ic_rd.svg';
import { ReactComponent as FacilityIcon } from '../assets/images/facility-icon.svg';

import { WEEK } from '../pages/dashboard/store/DashboardStore';
import DecryptHelper from '../helpers/decrypt';

const SidebarMobileToogler = () => (
  <div className="sidebar-mobile-toggler text-center">
    <button
      type="button"
      className="sidebar-mobile-main-toggle"
      onClick={() => document.body.classList.toggle('sidebar-mobile-main')}
      aria-label="icon-arrow"
    >
      <i className="icon-arrow-left8" />
    </button>
    Navigation
    <button
      className="sidebar-mobile-expand"
      type="button"
      onClick={() => document.getElementById('sidebar').classList.toggle('sidebar-fullscreen')}
      aria-label="icon-screen"
    >
      <i className="icon-screen-full" />
      <i className="icon-screen-normal" />
    </button>
  </div>
);

const SidebarNavItem = ({ name, link, Icon }) => (
  <li className="nav-item custom-nav">
    <NavLink to={link} className="nav-link" activeClassName="active">
      {Icon}
      <span>{name}</span>
    </NavLink>
  </li>
);

SidebarNavItem.propTypes = {
  name: PropTypes.string.isRequired,
  link: PropTypes.string.isRequired,
  Icon: PropTypes.element
};

SidebarNavItem.defaultProps = {
  Icon: <></>
};

const SidebarNavExternalItem = ({ name, link, Icon }) => (
  <li className="nav-item custom-nav">
    <Link to={{ pathname: link }} className="nav-link" target="_blank" rel="noopener noreferrer">
      {Icon}
      <span>{name}</span>
    </Link>
  </li>
);

SidebarNavExternalItem.propTypes = {
  name: PropTypes.string.isRequired,
  link: PropTypes.string.isRequired,
  Icon: PropTypes.element
};

SidebarNavExternalItem.defaultProps = {
  Icon: <></>
};

const SidebarNavItemSubMenu = ({ Icon, name, children, isOpen, setIsOpen }) => {
  const toggle = () => {
    if (!document.body.classList.contains('sidebar-xs')) {
      setIsOpen(isOpen === name ? '' : name);
    }
  };
  return (
    <li className="nav-item nav-item-submenu custom-submenu">
      <button
        onClick={toggle}
        className={`nav-link ${name === isOpen ? 'nav-item-open' : ''}`}
        type="button"
      >
        {Icon}
        <span>{name}</span>
      </button>
      <ul className={`nav nav-group-sub ${name === isOpen ? 'display-block' : ''}`}>{children}</ul>
    </li>
  );
};

SidebarNavItemSubMenu.propTypes = {
  name: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  Icon: PropTypes.element,
  isOpen: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  setIsOpen: PropTypes.func
};

SidebarNavItemSubMenu.defaultProps = {
  Icon: <></>,
  isOpen: false,
  setIsOpen: () => {}
};

const NavbarNav = ({ closeCallback }) => {
  const { togglePin, isPinned, checkPinned } = useSidebarContext();
  const [isOpenState, setIsOpen] = useState(isPinned);

  const toggle = () => {
    setIsOpen(!isOpen);
  };

  const isOpen = useMemo(() => {
    if (isPinned) return true;
    return isOpenState;
  }, [isOpenState, isPinned, checkPinned]);

  useEffect(() => {
    if (!isOpen) {
      document.body.classList.add('sidebar-xs');
      closeCallback();
    } else {
      document.body.classList.remove('sidebar-xs');
    }
    checkPinned();
  }, [isOpen]);

  return (
    <li className="nav-item custom-nav-item-lock" id="mobile-hide">
      <button
        type="button"
        onClick={toggle}
        className={`nav-link pb-0 main-icon open-icon ${isOpen}`}
        disabled={isPinned}
      >
        {isOpen ? (
          <LeftIcon className="sidebar-toggle-icon" />
        ) : (
          <RightIcon className="sidebar-toggle-icon" />
        )}
        {/* <i className="icon-paragraph-justify3" />
        <span>Navigation</span> */}
      </button>
      <button
        type="button"
        className={`nav-link pb-0 main-icon lock-icon ${isOpen}`}
        data-for="lock-icon"
        data-tip={isPinned ? 'Unpin sidebar' : 'Pin sidebar'}
        onClick={togglePin}
      >
        {isPinned ? <LockedIcon /> : <UnlockedIcon />}
      </button>
      <ReactTooltip id="lock-icon" className="protean-tooltip" place="bottom" />
      <div className="sidebar-devider sidebar-devider-protean" />
    </li>
  );
};

NavbarNav.propTypes = {
  closeCallback: PropTypes.func
};

NavbarNav.defaultProps = {
  closeCallback: () => {}
};

const SideBarCompoenent = () => {
  const [innerOpen, setInnerOpen] = useState();

  const hasAnyNotExternalRole = useMemo(() => {
    return existsAnyNotExternalRole(notOutUsers);
  }, []);

  const hasAnyNotExternalRoleWithLimitedInspector = useMemo(() => {
    return existsAnyNotExternalRole(notOutUsersWithLimitedInspector);
  }, []);

  const sessionExport = () => {
    const val = JSON.parse(localStorage.getItem('tokens'));
    const user = JSON.parse(localStorage.getItem('user'));
    if (val?.access_token) {
      val.userId = user?.current_user?.id;
      val.email = localStorage.getItem('username');
      const params = DecryptHelper.encryptWithKey(val);
      const url =
        params === ''
          ? 'https://inventoryvariance.proteanapp.com'
          : `https://inventoryvariance.proteanapp.com/fuel-system-lines?Session=${params}`;
      return url;
    }
    return 'https://inventoryvariance.proteanapp.com';
  };

  return (
    <div
      className="sidebar sidebar-light sidebar-main sidebar-expand-md align-self-start protean-sidebar"
      style={{ zIndex: '96' }}
      id="sidebar"
    >
      <SidebarMobileToogler />
      <div className="sidebar-content">
        <div className="card card-sidebar-mobile">
          <ul className="nav nav-sidebar" data-nav-type="accordion">
            <NavbarNav closeCallback={() => setInnerOpen()} />
            {/* <li className="nav-item-header">
              <div className="text-uppercase font-size-xs line-height-xs">Menu</div>
              <i className="icon-menu" title="Menu" />
            </li> */}
            {checkPerms('dashboard') && (
              <SidebarNavItem
                name="Dashboard"
                link="/dashboard"
                Icon={<DashboardIcon className="d-inline-flex" />}
              />
            )}

            {/* change permission check */}
            {checkFeatures('facility_profile.read') && (
              <SidebarNavItem
                name="Facility List"
                link="/facility_list"
                Icon={<FacilityIcon className="d-inline-flex" />}
              />
            )}

            {/* {checkFeatures('atg.separate_pages') && ( */}
            {checkFeatures('atg.enabled') && (
              <>
                <SidebarNavItemSubMenu
                  name="Remote Monitoring"
                  Icon={<ATGIcon className="d-inline-flex" />}
                  isOpen={innerOpen}
                  setIsOpen={setInnerOpen}
                >
                  <SidebarNavItem name="ATG Command Center" link="/remote_monitoring" />
                  <SidebarNavItem name="ATG Connection Center" link="/connection_center" />
                  {checkFeatures('atg.setup_center.access') && (
                    <SidebarNavItem name="ATG Setup Center" link="/setup_center" />
                  )}
                  <SidebarNavItem name="ATG Alarms" link="/alarms" />
                  <SidebarNavItem name="ATG Terminal Mode" link="/atg_terminal" />
                </SidebarNavItemSubMenu>
              </>
            )}
            {sideBarShow('workorders_enabled', '/workorders') && !isLimitedInspector() && (
              <SidebarNavItemSubMenu
                name="Work Order Summary"
                Icon={<WorkordersIcon className="d-inline-flex" />}
                isOpen={innerOpen}
                setIsOpen={setInnerOpen}
              >
                <SidebarNavItem name="Work Order Overview" link="/workorders" />
                {sideBarShow('workorders_enabled', '/lineitems') && (
                  <SidebarNavItem name="Line Item Overview" link="/lineitems" />
                )}
                {sideBarShow('quick_dispatch_enabled') && checkFeatures('quick_dispatch.read') && (
                  <SidebarNavItem name="Quick Dispatch" link="/max" />
                )}
                {sideBarShow('invoice_approval_enabled') &&
                  checkPerms('invoice_approval_enabled') && (
                    <SidebarNavItem name="Invoice approval" link="/invoice_approval" />
                  )}
              </SidebarNavItemSubMenu>
            )}

            {sideBarShow('release_detection_enabled') &&
              checkFeatures('release_detection.access') && (
                <SidebarNavItem
                  name="Release Detection"
                  link="/release_detection"
                  Icon={<RDIcon className="d-inline-flex" />}
                />
              )}

            {sideBarShow('quick_dispatch_enabled') &&
              !sideBarShow('workorders_enabled') &&
              checkFeatures('quick_dispatch.read') && (
                <SidebarNavItem
                  name="Quick Dispatch"
                  link="/max"
                  Icon={<WorkordersIcon className="d-inline-flex" />}
                />
              )}
            {/* <SidebarNavItem name="Issues" link="/issues" iconClass="mi-find-in-page" /> */}
            {/* <SidebarNavItem name="Inspections" link="/reports/all" iconClass="mi-folder-open" /> */}
            {checkPerms('file_cabinet_enabled') && !checkSingleRole('Contractor') && (
              <SidebarNavItem
                name="eCabinet"
                link="/e-cabinet"
                Icon={<EcabinetIcon className="d-inline-flex" />}
              />
            )}
            {/* permits only  client level/admin/superamin */}
            {/* <SidebarNavItem name="Permits" link="/permits" Icon={<DashboardIcon />} /> */}
            {sideBarShow('reports_enabled') && hasAnyNotExternalRole && (
              <SidebarNavItem
                name="Inspection Reports"
                link="/inspections"
                Icon={<InspectionReportsIcon className="d-inline-flex" />}
              />
            )}
            {checkPerms('reports_enabled') && hasAnyNotExternalRoleWithLimitedInspector && (
              <SidebarNavItemSubMenu
                name="Analytics Reports"
                Icon={<AnalyticsReportsIcon className="d-inline-flex" />}
                isOpen={innerOpen}
                setIsOpen={setInnerOpen}
              >
                <SidebarNavItem name="Analytics Report" link="/reports/analytics_report" />
                <SidebarNavItem name="Recap Report" link="/reports/recap" />
                <SidebarNavItem name="Remedy Report" link="/reports/remedy" />
                <SidebarNavItem name="Regulator Report" link="/reports/regulator" />
                <SidebarNavItem name="Hot Topic Report" link="/reports/hot_topic" />
                <SidebarNavItem
                  name="Cathodic Protection Report"
                  link="/reports/cathodic_protection"
                />
                <SidebarNavItem name="Widgets Report" link="/reports/widgets" />
                <SidebarNavItem
                  name="Monthly Comparison Report"
                  link="/reports/monthly_comparison_report"
                />
                {/* {showWalmartScorecard() && ( */}
                {/*  <SidebarNavItem */}
                {/*    name="Compliance Scorecard (beta)" */}
                {/*    link="/reports/compliance_scorecard" */}
                {/*  /> */}
                {/* )} */}
                {checkPerms('daily_overview_enabled') && (
                  <SidebarNavItem name="Daily Overview Report" link="/reports/daily_overview" />
                )}
                {checkFeatureToggles('periodic_report_enabled') && (
                  <SidebarNavItem
                    name="Periodic Overview Report"
                    link="/reports/periodic_overview"
                  />
                )}
                {checkFeatureToggles('acquisition_enabled') && (
                  <SidebarNavItem name="Acquisition report" link="/reports/acquisition" />
                )}
                {checkFeatureToggles('inventory_management_enabled') &&
                  checkFeatures('inventory_management.reports') && (
                    <SidebarNavItem
                      name="Inventory Management Reports"
                      link="/reports/inventory_management_reports"
                    />
                  )}
              </SidebarNavItemSubMenu>
            )}
            {/* {checkFeatureToggles('inventory_management_enabled') && */}
            {checkFeatures('inventory_management.access') &&
              checkFeatureToggles('inventory_management_enabled') && (
                <SidebarNavItem
                  name="Inventory Management"
                  link="/inventory_management"
                  Icon={<Inventory className="d-inline-flex" />}
                />
              )}
            {sideBarShow('scorecard_enabled') && hasAnyNotExternalRole && (
              <SidebarNavItemSubMenu
                name="Scorecard"
                Icon={<ScoreCardIcon className="d-inline-flex" />}
                isOpen={innerOpen}
                setIsOpen={setInnerOpen}
              >
                <SidebarNavItem name="Deficiency Specifics" link="/deficiency_specifics" />
                <SidebarNavItem name="Deficiency Trends" link="/deficiency" />
                <SidebarNavItem name="Performance Trends" link="/performance" />
                <SidebarNavItem name="Reports" link="/scorecard_report" />
              </SidebarNavItemSubMenu>
            )}
            {sideBarShow('analytics_enabled') && hasAnyNotExternalRole && (
              <SidebarNavItem
                name="Analytics"
                link="/analytics"
                Icon={<AnalyticsIcon className="d-inline-flex" />}
              />
            )}
            {sideBarShow('test_tracking_enabled') && hasAnyNotExternalRole && (
              <SidebarNavItem name="Test Tracking" link="/tests" Icon={<TestingIcon />} />
            )}
            {checkPerms('dashboard') && (
              <SidebarNavItem
                name="Calendar"
                link={`/calendar?type=${WEEK}&${WEEK}=${getCurrectWeekRange(0)}`}
                Icon={<CalendarIcon className="d-inline-flex" height={24} width={24} />}
              />
            )}
            {checkFeatures('regulatory_library_and_learning_management.access') && (
              <SidebarNavExternalItem
                name="Regulatory library"
                link="https://ctims.proteanapp.com"
                Icon={<CTIMSIcon />}
              />
            )}
            {checkFeatures('regulatory_library_and_learning_management.access') && (
              <SidebarNavExternalItem
                name="Inventory Variance"
                link={sessionExport()}
                Icon={<IVIcon />}
              />
            )}

            {checkFeatures('regulatory_library_and_learning_management.access') && (
              <SidebarNavItemSubMenu
                name="Learning Management"
                Icon={<LMSIcon />}
                isOpen={innerOpen}
                setIsOpen={setInnerOpen}
              >
                <SidebarNavExternalItem
                  name="A/B/C Operator Training"
                  link="https://training.petroclassroom.com/7g"
                />
                {/* <SidebarNavExternalItem */}
                {/*   name="Other Trainings" */}
                {/*   link="https://training.proteanapp.com" */}
                {/* /> */}
              </SidebarNavItemSubMenu>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
};

const SideBar = memo(SideBarCompoenent);
export default SideBar;
